import {VanComponent} from "@utils/vanComponent.ts";
import van, {type State} from "vanjs-core";
import {VanUtil as vu} from "@utils/vanUtil.ts";
import {LoginState} from "@login/loginState.ts";
import {Busy} from "@commons/layout/busy.ts";
import {Net} from "@utils/net.ts";
import type {SendResetPasswordCodeResponse} from "pnyx-dto";
import {Option} from "@utils/option.ts";
import {MessageDeliveryEnum} from "pnyx-dto";
const {div, a, progress, h1, p, form, label, input, button} = van.tags;

export interface IForgotPasswordSendCodeParent
{
    onReturnToSignIn(userName: string): void;
    onPromptUserForCode(verifyResponse: SendResetPasswordCodeResponse): void;
}

export type ForgotPasswordSendCodeOptions = 
{
    userName: string;
    parent: IForgotPasswordSendCodeParent;
}

type InternalForm =
{
    userName: State<string>;
    deliveryMethod: State<MessageDeliveryEnum|undefined>;
}

const deliveryOptions: Option<number>[] = Option.options(MessageDeliveryEnum, { ignoreDefaultValue: true });

export class ForgotPasswordSendCode extends VanComponent
{
    #parent: IForgotPasswordSendCodeParent;
    #form: InternalForm;
    private readonly progress:HTMLElement;
    
    constructor(args: ForgotPasswordSendCodeOptions) 
    {
        super();
        
        this.#parent = args.parent;
        
        this.#form = {
            userName: van.state(args.userName ?? ''),
            deliveryMethod: van.state(),
        };

        this.progress = Busy.visibility(progress({ "aria-label": "Submitting form" }));
    }
    
    toDom(): HTMLElement
    {
        return div(this.toDomTags({"class": "login-form-pane", style: 'padding-top: 10px'}),
            p({ class: 'login-kicker' }, 'Account Recovery'),
            h1({ class: 'login-title', id: 'login-title' }, 'Reset Password'),
            p({ class: 'text-secondary' }, "Enter your username and we'll send you a code to reset your password." ),
            form({ class: '', onsubmit: event => this.submit(event) },
                div({class: '' },
                    label({class: "login-label", for: 'login-username'}, 'Username'),
                    div({ class: 'login-input-wrap mt-1' },
                        input({ class: 'login-input form-control', id: 'login-username', tabIndex: 1,
                            type: 'text', required: 'required', autofocus: true,
                            spellcheck: false, autocapitalize: 'none',
                            value: () => this.#form.userName.val ?? '',
                            oninput: e => this.#form.userName.val = e.target.value
                        }),
                    ),
                    vu.load(vu.focalOnLoad)
                ),
                div({class: "" },
                    label({class: "login-label", for: 'login-delivery-method'}, 'Delivery Method'),
                    Option.select(this.#form.deliveryMethod, deliveryOptions, {class: 'form-select login-input  mt-1', required: 'required', id: 'login-delivery-method', tabIndex: 2 }, '--- Select Delivery ---'),
                ),
                div({ class: 'mt-2' },
                    button({ class: "login-submit", type: 'submit', tabIndex: 3 }, 'Send Code'),
                    div({class: "mt-1"}, this.progress),
                ),
                div({ class: "text-center" },
                    a({ class: 'link-plain text-body-secondary', onclick: () => this.#parent.onReturnToSignIn(this.#form.userName.val ?? '') }, 'Return to sign-in')
                )
            )
        );
    }
    
    async submit(event: Event): Promise<void>
    {
        LoginState.instance.alert.hide();
        event.preventDefault();  

        let response: SendResetPasswordCodeResponse = await Net.post<SendResetPasswordCodeResponse>({ 
            url: '/forgotPassword/sendCode', data: this.#form 
        });
        this.#parent.onPromptUserForCode(response);
    }
}